import { Vector } from 'mdatools/arrays';
import { qchisq } from 'mdatools/distributions';
import { mrange } from 'mdatools/stat';
import { getAxisTicks, getcolmap } from 'svelte-plots-basic/utils';

// default plot settings
export const MARGINS = [1.0, 1.1, 0.45, 0.4];

// default colors for SIMCA categories
export const CAT_COLORS = {
   'regular': '#2679B2',
   'extreme': '#F2B825',
   'outlier': '#D22C2F', 'outliers': '#D22C2F',
   'alien': '#2679B2', 'aliens': '#2679B2',
   'external': '#D22C2F'
};

// default colors for series
export const MARKERS = {'cal': 1, 'pv': 2, 'test': 2, 'new': 2};
export const LINE_COLORS = {'cal': '#2679B2', 'pv': '#D22C2F', 'test': '#a05e5e', 'new': '#5e85a0'};
export const FACE_COLORS = {'cal': '#f0f8ff', 'pv': '#f4e5e5', 'test': '#fff0f8', 'new': '#f0f8ff'};
export const FACE_TCOLORS = {'cal': '#2679B230', 'pv': '#D22C2F30', 'test': '#f05e5e30', 'new': '#5e85a030'};
export const LOADINGS_LINE_COLOR = '#808090';
export const LABELS_COLOR = '#a0a0a0';
export const LABELS_COLOR_SELECTED = '#909090';
export const AXIS_COLOR = '#909090';
export const MAX_LABEL_SYMBOLS = 8;

// default parameters to save plots as PNG file
export const PNG_WIDTH = 8;   // cm
export const PNG_HEIGHT = 8;  // cm
export const PNG_RES = 600;   // pixels per inch


/**
 * Generates axis title for given component and result object.
 * @param resobj - result object (single)
 * @param ncomp - component index (from 1)
 */
export function getAxisTitle(resobj, ncomp) {
   if (!resobj) return '';
   if (!ncomp || ncomp < 0 || ncomp > resobj.ncomp) return '';
   return `PC${ncomp} (${(resobj.stat.expvar[ncomp - 1] * 100).toFixed(1)}%)`;
}



/**
 * Take a subset of values without outliers.
 * @param {TypedArray} x - vector with selected variable values.
 * @param {Array} samples - row indices of samples without excluded outliers
 */
export function subset(x, samples) {
   if (x.length === samples.length) return x;
   const n = samples.length;
   const out = new x.constructor(n);
   for (let i in samples) {
      out[i] = x[samples[i]];
   }
   return out;
}


/**
 * Splits data values into n intervals and return a vector with interval boundaries.
 * @param {TypedArray} x -
 * @param n
 */
export function split(x, n) {
   let xMax = Number.NEGATIVE_INFINITY;
   let xMin = Number.POSITIVE_INFINITY;

   // compute min and max
   for (let i = 0; i < x.length; i++) {
      xMin = Math.min(xMin, x[i]);
      xMax = Math.max(xMax, x[i]);
   }

   // get nice values for colorbar ticks
   let ticks = getAxisTicks(undefined, [xMin, xMax], n - 1, true, false, 0).v;

   // add extra on left and right if ticks do not cover the whol range
   const dt = ticks[1] - ticks[0];
   if (ticks[0] > xMin) {
      const left = ticks[0] - dt;
      ticks = [left < xMin ? left : xMin, ...ticks];
   }
   if (ticks[ticks.length - 1] < xMax) {
      const right = ticks[ticks.length - 1] + dt;
      ticks = [...ticks, right > xMax ? right : xMax];
   }

   return ticks;
}


/**
 * Returns JSON with color for each class category
 * @param {Array} classNames - array with unique class names
 * @returns {Object}
 */
export function getClassColors(classNames) {
   const n = classNames.length;
   if (n < 1 || n > 16) {
      throw Error("getClassColors: can not create color map for more than 16 categories.");
   }

   const colors = getcolmap(n);
   const out = classNames.reduce((acc, curr, index) => {
      acc[curr] = colors[index];
      return acc;
   }, {});
   return out;
}


/**
 * Generates a vector of colors for each value in vector "x" depending on which interval its value falls into.
 * @param {TypedArray} x - vector with variable values (outliers free).
 * @param {Array} breaks - array with boundaries of the intervals.
 * @param {Array} colmap - array with color for each interval.
 *
 * @returns {Array} - same size as x, but with colors.
 */
export function getObjColors(x, breaks, colmap) {
   const n = breaks.length;

   // add a bit extra to right side of the last bin
   const colors = new Array(x.length);
   const delta = breaks[1] - breaks[0];
   // process the values
   for (let i = 0; i < x.length; i++) {
      for (let j = 0; j < n - 1; j++) {
         const left = x[i] >= breaks[j];
         const right = j === n - 2 ? x[i] < breaks[j + 1] + delta: x[i] < breaks[j + 1];
         if (left && right) {
            colors[i] = colmap[j];
            break;
         }
      }
   }

   return colors;
}


/**
 * Generates object with color group components dependending on the settings.
 * @param {Object} dataset - JSON with dataset (needed for color by variable).
 * @param {Object} resobj - object with results (needed for color by groups/classes).
 * @param {string} colorBy - type of grouping ('groups' or 'classes' vs. 'variable').
 * @param {number} colorByVar - index of variable to be used for grouping (starting from 1).
 * @param {string} resID - ID of results, needed for defining colors if no color grouping is selected (default option).
 * @param {Object} colorByObj - special JSON allowing to make manual color grouping.
 * @returns {Object} JSON with colors and component of colorbar legend; 'byClass' is true when the
 * legend labels are the class names.
 *
 * The parameter `colorByObj` may content:
 * - `values`: vector of values to use as groups
 * - `labels`: text labels for each unique value
 * - `classColMap`: JSON with label names and corresponding colors
 * - `filled`: optional, logical vector of same length as `values` setting if marker should be filled with line color.
 * - `showBar`: logical value, show or not colorbar legend (default `true`)
 *
 */
export function getColorGroups(dataset, resobj, colorBy, colorByVar, resID = 'cal', colorByObj) {

   if (!resobj) return null;

   let labels, map, breaks, colors, varname, filled, showBar = true, byClass = false;
   if ((colorBy === 'groups' || colorBy === 'classes') && resobj.classNames) {
      // color by groups
      const classColmap = getClassColors(resobj.classNames);

      // the map has to be built in the order of 'classNames', which is the order 'labels' below
      // and the ellipse group names are in. 'Object.values' can not be used for it: group names
      // are object keys here and javascript puts integer like keys first and in ascending order
      // whatever order they were added in, so numeric group names would pair the legend and the
      // ellipses with the colours of other groups while the points themselves stay right
      map = resobj.classNames.map(v => classColmap[v]);
      labels = resobj.classNames;
      byClass = true;
      colors = resobj.classes.map(v => classColmap[v]);
      breaks = Array.from({length: labels.length + 1}, (_, i) => i - 0.5);
      varname = null;
   } else if (colorBy === 'variable') {
      // color by variable
      const colorVar = subset(dataset.values.getcolref(colorByVar), resobj.objindices);
      varname = dataset.varlabels[colorByVar - 1];
      if (varname.length > 30) {
         varname = varname.substring(0, 29) + '...'; // limit varname by 30-32 symbols to fit the plot width
      }
      breaks = split(colorVar, 10);
      map = getcolmap(breaks.length - 1);
      colors = getObjColors(colorVar, breaks, map);

   } else if (colorByObj) {
      const values = colorByObj.values;
      labels = colorByObj.labels;
      const classColmap = colorByObj.classColmap;
      map = Object.values(classColmap);
      colors = values.map(v => classColmap[v]);
      breaks = Array.from({length: labels.length + 1}, (_, i) => i - 0.5);
      varname = null;

      if (colorByObj.filled && colorByObj.filled.length === values.length) {
         filled = colorByObj.filled;
      }

      if (colorByObj.showBar !== undefined) {
         showBar = colorByObj.showBar;
      }
   } else {
      // no color grouping
      labels = null;
      breaks = null;
      map = null;
      colors = Array(resobj.nrows).fill(LINE_COLORS[resID]);
   }

   return {
      labels, map, breaks, colors, varname, filled, showBar, byClass,
      mainColor: LINE_COLORS[resID],
      faceColor: FACE_COLORS[resID],
      faceTColor: FACE_TCOLORS[resID],
   };
}


/**
 * Computes coordinates of confidence ellipse ellipse.
 *
 * @param {Array} covMat - variance-covariance matrix for coordinates (xx, xy, yy).
 * @param {Array} center - coordinates of the center (x, y).
 * @param {Number} confLevel - confidence level (between 0 and 1).
 *
 * @returns {Array} array with six elements: x- and y-coordinates of the confidence ellipse
 * points (Vectors), smallest and largest values for each coordinates.
 */
export function getEllipse(covMat, center, confLevel = 0.95) {

   // define number of ellipse points and angle increments
   const nTheta = 100;
   const dTheta = 2 * Math.PI / (nTheta - 1);

   // compute eigenvalues
   const [a, b, d] = covMat;
   const D = Math.sqrt((a + d)**2 - 4 * (a * d - b * b));
   let l1 = 0.5 * (a + d - D);
   let l2 = 0.5 * (a + d + D);

   // swap eignevalues if needed so l1 ≥ l2
   if (l1 < l2) {
      let l = l1;
      l1 = l2;
      l2 = l;
   }

   // compute radii of the future ellipse
   const chisq = qchisq(confLevel, 2);
   const r1 = Math.sqrt(chisq * l1);
   const r2 = Math.sqrt(chisq * l2);

   let phi;
   if (Math.abs(b) < 10e-8) {
      phi = 0;
   } else {
      // compute largest eigenvactor and normalized it
      let v1x = -b / (a - l1);
      let v1y = 1;
      const n1 = Math.sqrt(v1x * v1x + v1y * v1y);
      v1x = v1x / n1;
      v1y = v1y / n1;
      phi = Math.atan2(v1y, v1x);
      if (phi < 0) phi = phi + 2 * Math.PI;
   }

   // get angle between the x-axis and the largest eigenvector

   // pre-compute cos and sin of the angle
   const cosphi = Math.cos(phi);
   const sinphi = Math.sin(phi);

   // prepare vectors for coordinates of the ellipse points
   const xVec = Vector.zeros(nTheta);
   const yVec = Vector.zeros(nTheta);

   // reset min/max values
   let xMin = Number.POSITIVE_INFINITY;
   let xMax = Number.NEGATIVE_INFINITY;
   let yMin = Number.POSITIVE_INFINITY;
   let yMax = Number.NEGATIVE_INFINITY;

   // compute and save coordinates of the ellipse points
   for (let i = 0; i < nTheta; i++) {
      const theta = i * dTheta;
      const x = r1 * Math.cos(theta);
      const y = r2 * Math.sin(theta);
      const xv = x * cosphi - y * sinphi + center[0];
      const yv = x * sinphi + y * cosphi + center[1];
      xVec.v[i] = xv;
      yVec.v[i] = yv;
      xMin = Math.min(xMin, xv);
      xMax = Math.max(xMax, xv);
      yMin = Math.min(yMin, yv);
      yMax = Math.max(yMax, yv);
   }

   return [xVec, yVec, xMin, xMax, yMin, yMax];
}


/**
 * Computes coordinates of confidence ellipses for each group of points.
 *
 * @param {Vector} xValues - x-coordinates of points (on scatter plot).
 * @param {Vector} yValues - y-coordinates of the points.
 * @param {Array} groups - array with group label for each point.
 * @param {Array} groupNames - array with unique group labels.
 * @param {Array} groupColors - array with color for each group (same as used for the points).
 * @param {Number} confLevel - confidence level (between 0 and 1).
 *
 * @returns {JSON} JSON containing coordinates and colors of the ellipses as well as
 * four values - min and max of x- and y-coordinates of the ellipses.
 */
export function getEllipses(xValues, yValues, groups, groupNames, groupColors, confLevel) {

   // check inputs
   if (!xValues || !yValues || !groups || !groupNames || !groupColors) return null;
   const n = xValues.length;
   const nGroups = groupNames.length;

   // for each group compute sum of coordinates and number of points
   const numPoints = new Array(nGroups).fill(0);   // number of point in each group
   const centers = new Array(nGroups * 2).fill(0); // 2 x nGroups - each column is a group

   for (let i = 0; i < n; i++) {

      // index of group the value belongs to
      const ind = groupNames.indexOf(groups[i])

      // number of points in and sum of coordinates in this group
      numPoints[ind] += 1;
      centers[ind * 2] += xValues.v[i];
      centers[ind * 2 + 1] += yValues.v[i];
   }

   // divide sum of coordinates to the number of groups to get centers
   for (let i = 0; i < nGroups; i++) {
      centers[i * 2] = centers[i * 2] / numPoints[i];
      centers[i * 2 + 1] = centers[i * 2 + 1] / numPoints[i];
   }

   // compute elements of variance-coveriance matrix
   const covMat = new Array(nGroups * 3).fill(0); // 3 x nGroups - each column is a group
   for (let i = 0; i < n; i++) {

      // index of group the value belongs to
      const ind = groupNames.indexOf(groups[i])

      // elements of variance/covariance matrix (xx, xy, yy)

      const x = (xValues.v[i] - centers[ind * 2]);
      const y = (yValues.v[i] - centers[ind * 2 + 1]);
      covMat[ind * 3]     += x * x;
      covMat[ind * 3 + 1] += x * y;
      covMat[ind * 3 + 2] += y * y;
   }

   // reset min/max values
   let minXVal = Number.POSITIVE_INFINITY;
   let maxXVal = Number.NEGATIVE_INFINITY;
   let minYVal = Number.POSITIVE_INFINITY;
   let maxYVal = Number.NEGATIVE_INFINITY;

   // divide sum of coordinates to the number of groups to get centers and compute coordinates of ellipses
   // a group of one point has no variance and a group of none has no centre either, so both give
   // NaN here and carry it into the limits of the whole plot, which then shows nothing. such a
   // group simply gets no ellipse - the points themselves are still drawn
   const ellipses = [];
   for (let i = 0; i < nGroups; i++) {
      if (numPoints[i] < 2) continue;

      const [xE, yE, xMin, xMax, yMin, yMax] = getEllipse(
         [covMat[i * 3] / (numPoints[i] - 1), covMat[i * 3 + 1] / (numPoints[i] - 1), covMat[i * 3 + 2] / (numPoints[i] - 1)],
         [centers[i * 2], centers[i * 2 + 1]],
         confLevel
      )
      // 'g' is the index of the group this ellipse belongs to - the position in the list is no
      // longer the same thing, since groups without an ellipse are not in it
      ellipses.push({x: xE, y: yE, c: groupColors[i], g: i});
      minXVal = Math.min(minXVal, xMin);
      maxXVal = Math.max(maxXVal, xMax);
      minYVal = Math.min(minYVal, yMin);
      maxYVal = Math.max(maxYVal, yMax);
   }

   // with no ellipse at all the min/max are still at their infinities, which must not reach the
   // axes - the caller is told there is no range to take into account instead
   if (ellipses.length === 0) {
      return {ellipses, xRange: null, yRange: null};
   }

   return {ellipses, xRange: [minXVal, maxXVal], yRange: [minYVal, maxYVal]};
}


/**
 * Captializes the first letter of the string.
 * @param {String} str
 * @returns {String}
 */
export function capt(str) {
   const firstLetter = str.charAt(0);
   const restString = str.substring(1);
   return firstLetter.toUpperCase() + restString;
}


/**
 * Compute coordinates for distance values.
 *
 * @param {Array} u - array or typedarray with distance values.
 * @param {number} Nu - degrees of freedom.
 * @param {number} u0 - scalar.
 * @param {string} transType - transformation type ('log' or 'none').
 *
 * @returns {Array} array with coordinates values, min and max value.
 */
export function getDistanceCoords(u, Nu, u0, transType) {

   if (!u || !Nu) return null;

   // prepare vectors for transformed distances
   const n = u.length;
   let values = Vector.zeros(n);
   let max = Number.NEGATIVE_INFINITY;
   let min = Number.POSITIVE_INFINITY;

   if (transType === 'log') {
      for (let i = 0; i < n; i++) {
         const v = Math.log(1 + u[i]/u0);
         values.v[i] = v;
         max = Math.max(max, v);
         min = Math.min(min, v);
      }
   } else {
      for (let i = 0; i < n; i++) {
         const v = u[i]/u0;
         values.v[i] = v;
         max = Math.max(max, v);
         min = Math.min(min, v);
      }
   }

   return {values, min, max}
}


/**
 * Returns text with axis label for distance plot.
 *
 * @param {string} id - distance id ('z', 'f', 'q', 'h').
 * @param {string} transType - transformation type ('log' or 'none').
 *
 * @returns {string}
 */
export function getDistanceAxisLabel(id, transType) {
   const distNames = {
      'z': 'Full y-distance',
      'f': 'Full x-distance',
      'q': 'Orthogonal distance',
      'h': 'Score distance',
   };
   return distNames[id] + ', ' + (transType === 'log' ? 'log(1 + ' + id + '/' + id + '0)' : id + '/' + id + '0');
};


/**
 * Computes coordinates of distance limit (outlier or extreme).
 *
 * @param {number} prob probability to compute the distance for (e.g. 0.95).
 * @param {number} Nx degrees of freedom for disance along x-axis.
 * @param {number} Ny degrees of freedom for disance along y-axis.
 * @param {string} transType transformation type ('log' or 'none').
 *
 * @returns {JSON} JSON with x and y coordinates.
 */
export function getDistanceLimitCoordinates(prob, Nx, Ny, transType) {
   const NPOINTS = 100;             // number of points in limit lines
   const Nt = Nx + Ny;
   const crit = qchisq(prob, Nt);

   // show limit for orthogonal and score distance
   const b0 = crit/Ny;
   const b1 = -Nx / Ny;
   const xMax = -b0 / b1;
   const x = Vector.seq(0, xMax * 1, xMax  / NPOINTS);
   const y = x.apply(v => v * b1 + b0);
   return {
      x:  transType === 'log' ? x.apply(v => Math.log(1 + v)) : x,
      y:  transType === 'log' ? y.apply(v => Math.log(1 + v)) : y
   }
}


/**
 * Convert array with values to text labels
 * @param v
 * @param mn
 * @param mx
 */
export function val2labels(v, mn, mx, maxDec = 4) {
   const diff = Math.abs(mx - mn);
   let dec;
   if (diff < 1) {
      dec = Math.min(Math.round(Math.abs(Math.log10(diff)) + 1), maxDec);
   } else if (diff < 10) {
      dec = 2;
   } else if (diff < 100) {
      dec = 1;
   } else {
      dec = 0;
   }

   return Array.from(v).map(x => x.toFixed(dec));
}


/**
 * Generates legend items for given set of results.
 *
 * @param {Array} resIDs - array with result IDs ('cal', 'pv', 'test', or 'new')
 * @param {Object} faceColors - optional, JSON with face color for each result.
 *
 * @returns {Array} array with legend items.
 */
export function getResLegend(resIDs, faceColors = FACE_TCOLORS) {
   if (!resIDs) return null;
   return resIDs.map(v => ({
      id: v,
      label: v,
      point: {
         marker: MARKERS[v],
         faceColor: faceColors[v],
         lineColor: LINE_COLORS[v],
         markerSize: MARKERS[v] === 1 ? 0.85 : 0.65,
         lineWidth: 1.5
      },
   }));
}


/**
 * Returns XAxis limits for plots, where x-values are series (e.g. wavenumbers).
 * @param {Vector} xValues
 * @param {string} plotType
 * @param {boolean} varrev
 */
export function getLimX(xValues, plotType, varrev) {
   if (!xValues) return [0, 1];
   if (plotType === 'p') return mrange(xValues);
   if (plotType === 'h') return xValues.length < 20 ? [0.5, xValues.length + 0.5] : mrange(xValues, 0.05);

   const xRange = mrange(xValues, 0);
   const dX = Math.abs((xRange[1] - xRange[0]) * 0.10);
   return varrev ? [xRange[1] + dX, xRange[0] - dX] : [xRange[0] - dX, xRange[1] + dX];
}
